import {type ReactNode} from 'react';
import {CopyIconButton} from './CopyableFieldRow';
import type {ExtractionDiagnosticoFields} from './ExtractionDiagnosticoSection';

/** Subconjunto do diagnóstico usado nos cartões (holerite / CTPS etc., sem import circular). */
export type DiagnosisForFieldCards = Partial<
    Pick<
        ExtractionDiagnosticoFields,
        | 'confianca_extracao'
        | 'confianca_classificacao'
        | 'qualidade_score'
        | 'justificativa'
        | 'confidence_scores'
    >
>;

function scoreToPercent0to100(value: number | null | undefined): number | null {
    if (value == null || Number.isNaN(Number(value))) return null;
    const n = Number(value);
    if (n >= 0 && n <= 1) return Math.round(n * 100);
    if (n >= 0 && n <= 100) return Math.round(n);
    return null;
}

/** Normaliza `confidence_scores`: chaves planas e, se houver um nível (ex. `dados`), também `pai.filho` e `filho`. */
export function flattenConfidenceScores(raw: unknown): Record<string, number> | null {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const root = raw as Record<string, unknown>;
    const out: Record<string, number> = {};

    for (const [k, v] of Object.entries(root)) {
        if (typeof v === 'number' && !Number.isNaN(v)) {
            out[k] = v;
        } else if (v != null && typeof v === 'object' && !Array.isArray(v)) {
            for (const [k2, v2] of Object.entries(v as Record<string, unknown>)) {
                if (typeof v2 === 'number' && !Number.isNaN(v2)) {
                    out[`${k}.${k2}`] = v2;
                    out[k2] = v2;
                }
            }
        }
    }
    return Object.keys(out).length > 0 ? out : null;
}

function stripAccents(s: string): string {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Chaves candidatas a partir do rótulo em português (ex.: "Data de nascimento" → data_de_nascimento). */
function labelToCandidateKeys(label: string): string[] {
    const t = stripAccents(label)
        .trim()
        .toLowerCase()
        .replace(/\s*—\s*/g, '_')
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    if (!t) return [];
    const compact = t.replace(/_/g, '');
    const keys = [t, compact].filter((x, i, a) => x && a.indexOf(x) === i);
    return keys;
}

function uniqueStrings(keys: (string | undefined | null)[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const k of keys) {
        const s = k?.trim();
        if (!s || seen.has(s)) continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}

/**
 * Percentual de confiança do campo: usa `confidence_scores` quando houver chave correspondente;
 * senão recua para escore global do arquivo com pequeno desvio por rótulo.
 */
export function fieldConfidencePercentFromDiagnostico(
    diagnostico: DiagnosisForFieldCards | null | undefined,
    label: string,
    scoreFieldKey?: string | string[] | null
): number | null {
    const scores = flattenConfidenceScores(diagnostico?.confidence_scores as unknown);
    const explicit = scoreFieldKey == null ? [] : Array.isArray(scoreFieldKey) ? scoreFieldKey : [scoreFieldKey];
    const fromLabel = labelToCandidateKeys(label);
    const keysToTry = uniqueStrings([...explicit, ...fromLabel]);

    if (scores && keysToTry.length > 0) {
        for (const key of keysToTry) {
            if (Object.prototype.hasOwnProperty.call(scores, key)) {
                const pct = scoreToPercent0to100(scores[key]);
                if (pct != null) return pct;
            }
        }
        for (const key of keysToTry) {
            const suffixMatch = Object.keys(scores).find(
                (k) => k === key || k.endsWith(`.${key}`) || (key.length > 1 && k.endsWith(`_${key}`))
            );
            if (suffixMatch != null) {
                const pct = scoreToPercent0to100(scores[suffixMatch]);
                if (pct != null) return pct;
            }
        }
    }

    const base =
        diagnostico?.confianca_extracao ??
        diagnostico?.confianca_classificacao ??
        diagnostico?.qualidade_score;
    const basePct = scoreToPercent0to100(base);
    if (basePct == null) return null;
    let salt = 0;
    for (let i = 0; i < label.length; i++) {
        salt = (salt + label.charCodeAt(i) * (i + 1)) % 15;
    }
    return Math.max(52, Math.min(100, basePct - salt));
}

export function fieldCardIaCopyFromDiagnostico(
    validated: boolean,
    diagnostico: DiagnosisForFieldCards | null | undefined
): {title: string; description: string} {
    const j = diagnostico?.justificativa?.trim();
    if (validated) {
        return {
            title: 'Pré-validação IA: Consistente',
            description: 'Formato e dígitos compatíveis com o padrão esperado.'
        };
    }
    return {
        title: 'Pré-validação IA: Divergência leve',
        description: j && j.length > 0 ? j : 'Confiança abaixo do limite recomendado; compare com o documento original.'
    };
}

/** Metadados de confiança / IA a partir do diagnóstico do arquivo e do rótulo do campo. */
export function extractionMetaForLabel(
    label: string,
    diagnostico?: DiagnosisForFieldCards | null,
    scoreFieldKey?: string | string[] | null
) {
    const confidencePct = fieldConfidencePercentFromDiagnostico(diagnostico, label, scoreFieldKey);
    const validated = true;
    const {title, description} = fieldCardIaCopyFromDiagnostico(validated, diagnostico);
    // const hasDiag = diagnostico != null;
    const showIa = false; //hasDiag && confidencePct != null;
    return {
        confidencePct,
        validated,
        iaTitle: title,
        iaDescription: description,
        showIa
    };
}

type DiagnosedExtractedFieldCardProps = {
    label: string;
    displayValue: ReactNode;
    copyText?: string | null;
    diagnostico?: DiagnosisForFieldCards | null;
    className?: string;
    /** Chave(s) em `confidence_scores` (ex.: `numero_documento`, `cpf_number`). */
    scoreFieldKey?: string | string[] | null;
};

/** Cartão de campo extraído com confiança / pré-validação IA ligadas ao `diagnostico` do arquivo. */
export function DiagnosedExtractedFieldCard({
    label,
    displayValue,
    copyText,
    diagnostico,
    className,
    scoreFieldKey
}: DiagnosedExtractedFieldCardProps) {
    const m = extractionMetaForLabel(label, diagnostico, scoreFieldKey);
    return (
        <ExtractedFieldCard
            label={label}
            displayValue={displayValue}
            copyText={copyText}
            confidencePct={m.confidencePct}
            validated={m.validated}
            iaTitle={m.iaTitle}
            iaDescription={m.iaDescription}
            showIaBlock={m.showIa}
            className={className}
        />
    );
}

export type ExtractedFieldCardProps = {
    label: string;
    /** Valor mostrado no campo (pode ser formatado) */
    displayValue: ReactNode;
    copyText?: string | null;
    /** 0–100; omitir ou null esconde barra e percentual */
    confidencePct?: number | null;
    /** default: confidencePct == null ? true : confidencePct >= 90 */
    validated?: boolean;
    iaTitle?: string;
    iaDescription?: string;
    className?: string;
    /** Quando false, omite o bloco de pré-validação IA (ex.: seção só leitura sem diagnóstico) */
    showIaBlock?: boolean;
    /** `neutral`: cartão simples (dados do cliente); `extraction`: cartão de campo extraído */
    tone?: 'extraction' | 'neutral';
    /** Sem copiar, sem botão de conferência; valor em bloco único (ex.: cliente / status no modal). */
    readOnly?: boolean;
};

export function ExtractedFieldCard({
    label,
    displayValue,
    copyText,
    confidencePct,
    validated: validatedProp,
    iaTitle,
    iaDescription,
    className = '',
    showIaBlock = true,
    tone = 'extraction',
    readOnly = false
}: ExtractedFieldCardProps) {
    const validated =
        validatedProp !== undefined
            ? validatedProp
            : confidencePct == null
              ? true
              : confidencePct >= 90;

    const cardTint =
        tone === 'neutral'
            ? 'bg-card border-border-subtle'
            : validated
              ? 'bg-card border-border-subtle'
              : 'bg-amber-500/10 border-amber-300/60 dark:border-amber-400/40';

    const iaBox =
        validated
            ? 'bg-emerald-500/10 border border-emerald-300/60 text-emerald-800 dark:text-emerald-200'
            : 'bg-amber-500/10 border border-amber-300/60 text-amber-800 dark:text-amber-200';
    const canCopy =
        !readOnly &&
        copyText != null &&
        String(copyText).trim() !== '' &&
        String(copyText).trim() !== '—';
    const reviewStatusClasses = 'border-amber-300/60 bg-amber-500/10 text-amber-800 dark:text-amber-200';

    return (
        <div className={`rounded-md border p-2.5 ${cardTint} ${className}`}>
            <div className="flex items-center justify-between gap-3">
                <p className="font-instrument text-[11px] uppercase tracking-wide text-textSecondary">
                    {label}
                </p>
                <div className="flex shrink-0 items-center gap-1">
                    {tone === 'extraction' && !validated ? (
                        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${reviewStatusClasses}`}>
                            Revisão
                        </span>
                    ) : null}
                    {canCopy ? (
                        <CopyIconButton
                            text={String(copyText)}
                            ariaLabel={`Copiar ${label}`}
                            className="h-6 w-6"
                        />
                    ) : null}
                </div>
            </div>

            <div className="mt-1 font-instrument text-sm font-medium text-textPrimary break-words">
                {displayValue}
            </div>

            {confidencePct != null ? (
                <p className="mt-0.5 font-instrument text-[11px] text-textSecondary">
                    OCR · Confiança {confidencePct}%
                </p>
            ) : null}

            {showIaBlock && (
                <div className={`mt-2 rounded-md px-3 py-2 text-xs font-instrument leading-relaxed ${iaBox}`}>
                    <p className="font-semibold flex items-center gap-1.5 mb-1">
                        <i className="ri-robot-line text-base shrink-0" aria-hidden />
                        {iaTitle}
                    </p>
                    <p className="opacity-95">{iaDescription}</p>
                </div>
            )}
        </div>
    );
}

/** Cartão só leitura (cliente / status do processo no modal — sem copiar nem conferência). */
export function SimpleFieldCard({
    label,
    displayValue
}: {
    label: string;
    displayValue: ReactNode;
}) {
    return (
        <ExtractedFieldCard
            label={label}
            displayValue={displayValue}
            confidencePct={null}
            validated
            showIaBlock={false}
            tone="neutral"
            readOnly
        />
    );
}
